import React, { CSSProperties } from 'react';
import {
  DraftConfigurationText,
  DraftConfigurationTextStyle,
  DraftTextOrPlaceholderTextConfiguration,
} from '@/types';
import { textAlignToAnchor } from '@/utils/text/textAlignToAnchor';
import { scaleSize } from '@/model/configuration/scaleSize';
import { getFillValue } from '../../utils/getFillValue';
import { PatternList } from '../PatternList';
import { SoftBoundary } from '../SoftBoundary';

interface TSpan {
  x: number;
  y: number;
  text: string;
}

const tspanStyles = {
  userSelect: 'none',
  WebkitUserSelect: 'none',
  whiteSpace: 'pre',
} as CSSProperties;

const getTspansFromConfiguration = (
  { content }: DraftConfigurationText,
  style: DraftConfigurationTextStyle,
  scale: number
) => {
  return content.split('\n').map((line, lineIndex) => {
    const tspan: TSpan = {
      text: line,
      x: style.x * scale,
      y: style.y * scale + lineIndex * style.fontSize * scale * 1.2,
    };
    return tspan;
  });
};

export const useText = (
  configuration: DraftTextOrPlaceholderTextConfiguration,
  offset = configuration.offset,
  rotation = configuration.rotation,
  scale = configuration.scale
) => {
  const { cid: configurationId, printArea, text, size: textSize } = configuration;
  const textStyle = text.style;
  const scaledSize = scaleSize(textSize, scale);
  const center = {
    x: scaledSize.width / 2,
    y: scaledSize.height / 2,
  };

  const tspans: TSpan[] = getTspansFromConfiguration(text, textStyle, scale);

  const textElement = (
    <SoftBoundary
      cid={configurationId}
      printArea={printArea}
      rotation={rotation}
      size={textSize}
      offset={offset}
    >
      <g>
        <PatternList
          configuration={configuration}
          width={textSize.width}
          height={textSize.height}
        />
        <text textRendering="geometricPrecision" letterSpacing="0px">
          {tspans.map((span, i) => (
            <tspan
              key={i}
              x={span.x}
              y={span.y}
              fill={getFillValue(configuration)}
              fontSize={textStyle.fontSize * scale}
              fontWeight={textStyle.font.weight}
              fontStyle={textStyle.font.style}
              textAnchor={textAlignToAnchor(textStyle.textAlign)}
              fontFamily={`${textStyle.font.identifier}`}
              style={tspanStyles}
            >
              {span.text || ' '}
            </tspan>
          ))}
        </text>
      </g>
    </SoftBoundary>
  );

  const transform = `translate(${offset.x}, ${offset.y}) rotate(${rotation}, ${center.x}, ${center.y})`;

  return [textElement, transform] as const;
};
